import { React, useEffect, useState } from 'react';
import { Box, Card, CardContent, Container, Grid, Typography, Link } from '@mui/material';
import { Line } from 'react-chartjs-2';
import Slider from 'react-slick';
import NavigationBar from '../navbar/NavigationBar';
import Footer from '../navbar/Footer';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import BabyChangingStationIcon from '@mui/icons-material/BabyChangingStation';
import GroupsIcon from '@mui/icons-material/Groups';
import PartnersSlider from '../Partners/PartnersSlider';
import EventIcon from '@mui/icons-material/Event';
import {
    Chart as ChartJS,
    LinearScale,
    PointElement,
    LineElement,
    ArcElement,
    Tooltip,
    Legend,
    Title,
    CategoryScale,
} from 'chart.js';
import { getLatestPublications } from '../services/latestPublicationService';
import moment from 'moment';

ChartJS.register(
    LinearScale,
    PointElement,
    LineElement,
    ArcElement,
    Tooltip,
    Legend,
    Title,
    CategoryScale
);

const Home = () => {
    const [data1, setData1] = useState(null);
    const [data2, setData2] = useState(null);
    const [data3, setData3] = useState(null);
    const [data4, setData4] = useState(null); // State pour ICE
    const [icaCommerceData, setIcaCommerceData] = useState(null);
    const [icaServiceData, setIcaServiceData] = useState(null);
    const [visiblePoints, setVisiblePoints] = useState({});
    const [latestPublications, setLatestPublications] = useState([]);
    const [loadingPublications, setLoadingPublications] = useState(true);

    const sliderSettings = {
        dots: true,
        infinite: true,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        autoplay: true,
        autoplaySpeed: 3000,
    };

    useEffect(() => {
        // Chargement des publications récentes
        getLatestPublications()
            .then((publications) => {
                setLatestPublications(publications);
                setLoadingPublications(false);
            })
            .catch((err) => {
                console.error("Erreur lors du chargement des publications récentes : ", err);
                setLoadingPublications(false);
            });

        fetch("/ipc.json")
            .then(response => response.json())
            .then(data => {
                const convertedData = convertirDonnees(data);
                setData1(convertedData);
            })
            .catch(error => {
                console.error("Erreur lors du chargement des données IPC : ", error);
            });

        fetch("/pib.json")
            .then(response => response.json())
            .then(data => {
                const convertedData = convertirDonneesPIB(data);
                setData2(convertedData);
            })
            .catch(error => {
                console.error("Erreur lors du chargement des données PIB : ", error);
            });

        fetch("/icclb.json")
            .then(response => response.json())
            .then(data => {
                const convertedData = convertirDonneesICCLB(data);
                setData3(convertedData);
            })
            .catch(error => {
                console.error("Erreur lors du chargement des données ICCLB : ", error);
            });

        fetch("/icaCommerce.json")
            .then(response => response.json())
            .then(data => {
                const convertedData = convertirDonneesICACommerce(data);
                setIcaCommerceData(convertedData);
            })
            .catch(error => {
                console.error("Erreur lors du chargement des données ICA COMMERCE : ", error);
            });

        fetch("/icaService.json")
            .then(response => response.json())
            .then(data => {
                const convertedData = convertirDonneesICAService(data);
                setIcaServiceData(convertedData);
            })
            .catch(error => {
                console.error("Erreur lors du chargement des données ICA Service : ", error);
            });

        fetch("/ice.json")
            .then(response => response.json())
            .then(data => {
                const convertedData = convertirDonneesICE(data);
                setData4(convertedData);
            })
            .catch(error => {
                console.error("Erreur lors du chargement des données ICE : ", error);
            });
    }, []);

    function convertirDonnees(donnees) {
        if (!donnees || !Array.isArray(donnees) || donnees.length === 0) {
            return { labels: [], datasets: [] };
        }

        const premierObjet = donnees[0];
        const dates = Object.keys(premierObjet).filter(key => key !== "Groupes");
        const labels = dates.map(dateStr => {
            const [year, month, day] = dateStr.split('-');
            return new Date(parseInt(year), parseInt(month) - 1, parseInt(day)).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long' });
        });

        const datasets = donnees.map((groupeData, index) => {
            const label = groupeData.Groupes;
            const data = dates.map(dateKey => parseFloat(groupeData[dateKey].replace(',', '.')));
            const isLastDataset = index === donnees.length - 1;

            return {
                label: label,
                data: data,
                borderColor: isLastDataset ? 'rgba(165, 42, 42, 1)' : 'rgba(128, 128, 128, 1)',
                backgroundColor: isLastDataset ? 'rgba(165, 42, 42, 0.2)' : 'rgba(128, 128, 128, 0.2)',
                fill: true,
                borderWidth: 2,
                tension: 0.6,
                pointRadius: visiblePoints[index] ? 5 : 0,
                pointHoverRadius: visiblePoints[index] ? 16 : 6,
                pointBackgroundColor: visiblePoints[index] ? (isLastDataset ? 'rgba(165, 42, 42, 1)' : 'rgba(128, 128, 128, 1)') : 'transparent',
            };
        });

        return {
            labels: labels,
            datasets: datasets,
        };
    }

    function convertirDonneesICE(donnees) {
        if (!donnees || !Array.isArray(donnees) || donnees.length === 0) {
            return { labels: [], datasets: [] };
        }

        const couleurs = [
            'rgb(255, 99, 132)',
            'rgb(54, 162, 235)',
            'rgb(255, 206, 86)',
            'rgb(75, 192, 192)',
            'rgb(153, 102, 255)',
            'rgb(255, 159, 64)',
            'rgb(201, 203, 207)',
            'rgb(102, 255, 102)',
            'rgb(255, 102, 255)',
            'rgb(0, 204, 204)'
        ];

        const dates = Object.keys(donnees[0]).filter(key => key !== "Groupes");
        const sortedDates = dates.sort((a, b) => {
            const yearA = parseInt(a.substring(0, 4));
            const quarterA = parseInt(a.substring(5));
            const yearB = parseInt(b.substring(0, 4));
            const quarterB = parseInt(b.substring(5));
            return yearA === yearB ? quarterA - quarterB : yearA - yearB;
        });

        const labels = sortedDates.map(dateStr => dateStr);

        const datasets = donnees.map((groupeData, index) => {
            const label = groupeData.Groupes;
            const data = sortedDates.map(dateKey => parseFloat(groupeData[dateKey].replace(',', '.')) || 0);
            const colorIndex = index % couleurs.length;
            return {
                label: label,
                data: data,
                borderColor: couleurs[colorIndex],
                backgroundColor: `rgba(${couleurs[colorIndex].match(/\d+/g).join(',')}, 0.2)`,
                fill: true,
                borderWidth: 2,
                tension: 0.4,
                pointRadius: visiblePoints[index] ? 5 : 0,
                pointHoverRadius: visiblePoints[index] ? 10 : 6,
                pointBackgroundColor: visiblePoints[index] ? couleurs[colorIndex] : 'transparent',
            };
        });

        return { labels: labels, datasets: datasets };
    }

    function convertirDonneesPIB(donnees) {
        if (!donnees || !Array.isArray(donnees) || donnees.length === 0) {
            return { labels: [], datasets: [] };
        }

        const premierObjet = donnees[0];
        const dates = Object.keys(premierObjet).filter(key => key !== "Groupes");
        const labels = dates.map(dateStr => {
            const [year, month, day] = dateStr.split('-');
            return new Date(parseInt(year), parseInt(month) - 1, parseInt(day)).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long' });
        });

        const datasets = donnees.map((groupeData, index) => {
            const label = groupeData.Groupes;
            const data = dates.map(dateKey => parseFloat(groupeData[dateKey].replace(',', '.')));
            const isLastDataset = index === donnees.length - 1;

            return {
                label: label,
                data: data,
                borderColor: isLastDataset ? 'rgba(165, 42, 42, 1)' : 'rgba(128, 128, 128, 1)',
                backgroundColor: isLastDataset ? 'rgba(75, 12, 248, 0.2)' : 'rgba(120, 8, 247, 0.2)',
                fill: true,
                borderWidth: 2,
                tension: 0.6,
                pointRadius: visiblePoints[index] ? 5 : 0,
                pointHoverRadius: visiblePoints[index] ? 16 : 6,
                pointBackgroundColor: visiblePoints[index] ? (isLastDataset ? 'rgba(165, 42, 42, 1)' : 'rgba(128, 128, 128, 1)') : 'transparent',
            };
        });

        return {
            labels: labels,
            datasets: datasets,
        };
    }

    function convertirDonneesICCLB(donnees) {
        if (!donnees || !Array.isArray(donnees) || donnees.length === 0) {
            return { labels: [], datasets: [] };
        }

        const premierObjet = donnees[0];
        const dates = Object.keys(premierObjet).filter(key => key !== "Groupes");
        const labels = dates.map(dateStr => {
            const [year, month, day] = dateStr.split('-');
            return new Date(parseInt(year), parseInt(month) - 1, parseInt(day)).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long' });
        });

        const datasets = donnees.map((groupeData, index) => {
            const label = groupeData.Groupes;
            const data = dates.map(dateKey => parseFloat(groupeData[dateKey].replace(',', '.')));
            const isLastDataset = index === donnees.length - 1;

            return {
                label: label,
                data: data,
                borderColor: isLastDataset ? 'rgba(165, 42, 42, 1)' : 'rgba(128, 128, 128, 1)',
                backgroundColor: isLastDataset ? 'rgba(75, 12, 248, 0.2)' : 'rgba(120, 8, 247, 0.2)',
                fill: true,
                borderWidth: 2,
                tension: 0.6,
                pointRadius: visiblePoints[index] ? 5 : 0,
                pointHoverRadius: visiblePoints[index] ? 16 : 6,
                pointBackgroundColor: visiblePoints[index] ? (isLastDataset ? 'rgba(165, 42, 42, 1)' : 'rgba(128, 128, 128, 1)') : 'transparent',
            };
        });

        return {
            labels: labels,
            datasets: datasets,
        };
    }

    function convertirDonneesICACommerce(donnees) {
        if (!donnees || !Array.isArray(donnees) || donnees.length === 0) {
            return { labels: [], datasets: [] };
        }

        const premierObjet = donnees[0];
        const dates = Object.keys(premierObjet).filter(key => key !== "Groupes");
        const labels = dates.map(dateStr => dateStr);

        const datasets = donnees.map((secteurData, index) => {
            const label = secteurData.Groupes;
            const data = dates.map(dateKey => parseFloat(secteurData[dateKey].replace(',', '.')));

            return {
                label: label,
                data: data,
                borderColor: index === 0 ? 'rgba(54, 162, 235, 1)' : 'rgba(255, 99, 132, 1)',
                backgroundColor: index === 0 ? 'rgba(54, 162, 235, 0.2)' : 'rgba(255, 99, 132, 0.2)',
                fill: true,
                borderWidth: 2,
                tension: 0.6,
                pointRadius: visiblePoints[index] ? 5 : 0,
                pointHoverRadius: visiblePoints[index] ? 16 : 6,
                pointBackgroundColor: visiblePoints[index] ? (index === 0 ? 'rgba(54, 162, 235, 1)' : 'rgba(255, 99, 132, 1)') : 'transparent',
            };
        });

        return {
            labels: labels,
            datasets: datasets,
        };
    }

    function convertirDonneesICAService(donnees) {
        if (!donnees || !Array.isArray(donnees) || donnees.length === 0) {
            return { labels: [], datasets: [] };
        }

        const premierObjet = donnees[0];
        const dates = Object.keys(premierObjet).filter(key => key !== "Groupes");
        const labels = dates.map(dateStr => dateStr);

        const datasets = donnees.map((secteurData, index) => {
            const label = secteurData.Groupes;
            const data = dates.map(dateKey => parseFloat(secteurData[dateKey].replace(',', '.')));

            return {
                label: label,
                data: data,
                borderColor: index === 0 ? 'rgba(75, 192, 192, 1)' : 'rgba(255, 206, 86, 1)',
                backgroundColor: index === 0 ? 'rgba(75, 192, 192, 0.2)' : 'rgba(255, 206, 86, 0.2)',
                fill: true,
                borderWidth: 2,
                tension: 0.6,
                pointRadius: visiblePoints[index] ? 5 : 0,
                pointHoverRadius: visiblePoints[index] ? 16 : 6,
                pointBackgroundColor: visiblePoints[index] ? (index === 0 ? 'rgba(75, 192, 192, 1)' : 'rgba(255, 206, 86, 1)') : 'transparent',
            };
        });

        return {
            labels: labels,
            datasets: datasets,
        };
    }

    const handleClick = (event, chartElement) => {
        if (!chartElement || chartElement.length === 0) return;
        const index = chartElement[0].datasetIndex;
        setVisiblePoints(prev => {
            const newVisibility = !prev[index];
            return { ...prev, [index]: newVisibility };
        });
    };

    const chartOptions = {
        responsive: true,
        plugins: {
            legend: {
                position: 'bottom',
                onClick: (e, legendItem, legend) => {
                    e.stopPropagation();
                    const index = legendItem.datasetIndex;
                    setVisiblePoints(prev => {
                        const newVisibility = !prev[index];
                        return { ...prev, [index]: newVisibility };
                    });
                }
            },
            tooltip: {
                callbacks: {
                    label: function (tooltipItem) {
                        return `${tooltipItem.dataset.label}: ${tooltipItem.raw}`;
                    }
                },
                displayColors: true,
                usePointStyle: true,
            },
        },
        interaction: {
            mode: 'nearest',
            intersect: false,
        },
        scales: {
            y: {
                title: {
                    display: true,
                    text: 'Valeurs'
                },
                ticks: {
                    callback: function (value) {
                        return value.toLocaleString('fr-FR');
                    },
                    stepSize: 10,
                },
                min: 0,
            },
            x: {
                title: {
                    display: true,
                    text: 'Période'
                },
                grid: {
                    display: false
                }
            }
        },
        elements: {
            line: {
                borderWidth: 3,
            },
            point: {
                hoverRadius: 8,
                hoverBorderWidth: 2,
            }
        },
        onClick: handleClick
    };

    return (
        <div>
            <NavigationBar />
            <br />
            <Box sx={{ my: 4 }}>
                <Slider {...sliderSettings}>
                    <div>
                        <Card sx={{ padding: 2, textAlign: 'center' }}>
                            <Typography variant="body2">
                                <Link href="/resultatRGPHAE2024" underline="hover" target="_blank" sx={{ color: ' #008D36' }}>
                                    Résultats sommaires RGPHAE 2024
                                </Link>
                            </Typography>
                        </Card>
                    </div>
                    <div>
                        <Card sx={{ padding: 2, textAlign: 'center' }}>
                            <Typography variant="body2">
                                <Link href="/RGPHAEDataPage" underline="hover" target="_blank" sx={{ color: ' #008D36' }}>
                                    Résultats détaillés RGPHAE 2024
                                </Link>
                            </Typography>
                        </Card>
                    </div>
                    <div>
                        <Card sx={{ padding: 2, textAlign: 'center' }}>
                            <Typography variant="body2">
                                <Link href="/populationDataPageBurundi" underline="hover" target="_blank" sx={{ color: ' #008D36' }}>
                                    Pop. par Provinces / Communes / Collines RGPHAE 2024
                                </Link>
                            </Typography>
                        </Card>
                    </div>
                </Slider>
            </Box>
            <Container sx={{ my: 1 }}>
                <Grid container spacing={2} sx={{
                    borderRadius: 2,
                    boxShadow: 2,
                    height: '100%',
                    backgroundColor: '#f9f9f9',
                    borderColor: '#FF3D3D',
                }}>
                    {/* Partie supérieure avec le carousel de graphiques */}
                    <Grid item xs={12} md={6}>
                        <Typography variant="h5" fontWeight="bold" gutterBottom>
                            Articles récents
                        </Typography>
                        <Card variant="outlined" sx={{ borderRadius: 2, boxShadow: 3, mb: 2 }}>
                            <CardContent>
                                <Slider {...sliderSettings}>
                                    <div>
                                        <Typography variant="subtitle1" fontWeight="bold" textAlign="center" gutterBottom>
                                            Indice des Prix à la Consommation (IPC)
                                        </Typography>
                                        {data1 &&
                                            <Line
                                                data={data1}
                                                options={chartOptions}
                                                height={220}
                                            />
                                        }
                                        <Typography variant="caption" sx={{ mt: 2, display: 'block', textAlign: 'center' }}>
                                            <Link href="/documents/bases/ipc.xlsx" style={{ color: 'green' }}>Télécharger le fichier excel IPC</Link>
                                        </Typography>
                                    </div>
                                    <div>
                                        <Typography variant="subtitle1" fontWeight="bold" textAlign="center" gutterBottom>
                                            Produit Intérieur Brut (PIB)
                                        </Typography>
                                        {data2 &&
                                            <Line
                                                data={data2}
                                                options={chartOptions}
                                                height={220}
                                            />
                                        }
                                        <Typography variant="caption" sx={{ mt: 2, display: 'block', textAlign: 'center' }}>
                                            <Link href="/documents/bases/GROSSDOMESTICPRODUCT.xlsx" style={{ color: 'green' }}>Télécharger le fichier excel PIB</Link>
                                        </Typography>
                                    </div>
                                    <div>
                                        <Typography variant="subtitle1" fontWeight="bold" textAlign="center" gutterBottom>
                                            Indice du Coût de la Construction des Logements à Bujumbura (ICCLB)
                                        </Typography>
                                        {data3 &&
                                            <Line
                                                data={data3}
                                                options={chartOptions}
                                                height={220}
                                            />
                                        }
                                        <Typography variant="caption" sx={{ mt: 2, display: 'block', textAlign: 'center' }}>
                                            <Link href="/documents/bases/ICCLB.xlsx" style={{ color: 'green' }}>Télécharger le fichier excel ICCLB</Link>
                                        </Typography>
                                    </div>
                                    <div>
                                        <Typography variant="subtitle1" fontWeight="bold" textAlign="center" gutterBottom>
                                            Indice du Chiffre d'Affaires du Commerce (ICA Commerce)
                                        </Typography>
                                        {icaCommerceData &&
                                            <Line
                                                data={icaCommerceData}
                                                options={chartOptions}
                                                height={220}
                                            />
                                        }
                                        <Typography variant="caption" sx={{ mt: 2, display: 'block', textAlign: 'center' }}>
                                            <Link href="/documents/bases/ICACOMMERCE.xlsx" style={{ color: 'green' }}>Télécharger le fichier excel ICA -Commerce</Link>
                                        </Typography>
                                    </div>
                                    <div>
                                        <Typography variant="subtitle1" fontWeight="bold" textAlign="center" gutterBottom>
                                            Indice du Chiffre d'Affaires des Services (ICA Service)
                                        </Typography>
                                        {icaServiceData &&
                                            <Line
                                                data={icaServiceData}
                                                options={chartOptions}
                                                height={220}
                                            />
                                        }
                                        <Typography variant="caption" sx={{ mt: 2, display: 'block', textAlign: 'center' }}>
                                            <Link href="/documents/bases/ICASERVICE.xlsx" style={{ color: 'green' }}>Télécharger le fichier excel -ICA Service</Link>
                                        </Typography>
                                    </div>
                                    <div>
                                        <Typography variant="subtitle1" fontWeight="bold" textAlign="center" gutterBottom>
                                            Indice du Commerce Extérieur (ICE)
                                        </Typography>
                                        {data4 &&
                                            <Line
                                                data={data4}
                                                options={chartOptions}
                                                height={220}
                                            />
                                        }
                                        <Typography variant="caption" sx={{ mt: 2, display: 'block', textAlign: 'center' }}>
                                            <Link href="/documents/bases/ICE.xlsx" style={{ color: 'green' }}>Télécharger le fichier Excel ICE</Link>
                                        </Typography>
                                    </div>
                                </Slider>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Section Chiffres Clés */}
                    <Grid item xs={12} md={6}>
                        <Typography variant="h5" fontWeight="bold" gutterBottom>
                            Chiffres Clés
                        </Typography>
                        {[
                            { title: 'Produit intérieur brut (PIB)', icon: <MonetizationOnIcon sx={{ color: ' #008D36', fontSize: '60px' }} />, description: '6 217,0 milliards de BIF (2019)', value: '6,217.0', link: 'comptesNationaux' },
                            { title: 'Inflation en moyenne annuelle', icon: <ShoppingCartIcon sx={{ color: ' #008D36', fontSize: '60px' }} />, description: 'Décembre 2013=100 (Janvier 2026)', value: '+31,6%', link: 'indicesPrixConsommation' },
                            { title: 'Espérance de vie à la naissance', icon: <BabyChangingStationIcon sx={{ color: ' #008D36', fontSize: '60px' }} />, description: '61,2 ans (Projections INSBU, 2024)', value: '61,2', link: 'enquenteSanteDemographe' },
                            { title: 'Taille de la population résidente', icon: <GroupsIcon sx={{ color: ' #008D36', fontSize: '60px' }} />, description: '12,332,788 habitants (cfr RGPHAE 2024)', value: '12,332,788', link: 'resultatRGPHAE2024' },
                        ].map((keyData, index) => (
                            <Link href={keyData.link} key={index} sx={{ textDecoration: 'none' }}>
                                <Card sx={{ mb: 2, padding: 0 }} variant="outlined">
                                    <CardContent>
                                        <Grid container alignItems="center">
                                            <Grid item xs={6}>
                                                <Typography variant="body1" fontWeight="bold" sx={{ color: 'green' }}>
                                                    {keyData.title}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {keyData.description}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={3} sx={{ textAlign: 'center' }}>
                                                {keyData.icon}
                                            </Grid>
                                            <Grid item xs={3} sx={{ textAlign: 'right', backgroundColor: 'green', borderRadius: 1, padding: 1 }}>
                                                <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '16px', color: 'white' }}>
                                                    {keyData.value}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Link>
                        ))}
                    </Grid>
                </Grid>

                <Box sx={{ mt: 2 }}>
                    <Grid container spacing={2}>
                        {['Publications récentes', 'Calendrier de diffusion'].map((sectionTitle, index) => (
                            <Grid item xs={12} sm={6} md={6} key={index}>
                                <Card
                                    variant="outlined"
                                    sx={{
                                        borderRadius: 2,
                                        boxShadow: 2,
                                        height: '100%',
                                        backgroundColor: '#f9f9f9',
                                        transition: 'transform 0.2s'
                                    }}
                                    onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                                    onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                                >
                                    <CardContent sx={{ flexGrow: 1 }}>
                                        <Typography
                                            variant="h6"
                                            fontWeight="bold"
                                            sx={{ color: 'black', marginBottom: 2 }}
                                        >
                                            {sectionTitle}
                                        </Typography>
                                        <ul>
                                            {sectionTitle === 'Publications récentes' && (
                                                <>
                                                    {loadingPublications ? (
                                                        <li>Chargement des publications...</li>
                                                    ) : latestPublications.length > 0 ? (
                                                        latestPublications.map((pub, i) => (
                                                            <li key={i} style={{ marginBottom: '12px' }}>
                                                                <Typography variant="body2" color="textSecondary">
                                                                    {moment(pub.publication_date).format('MMMM YYYY')}
                                                                </Typography>
                                                                <Link
                                                                    href={pub.document_url}
                                                                    underline="hover"
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    sx={{ color: 'black', '&:hover': { textDecoration: 'underline', color: '#3DDC84' } }}
                                                                >
                                                                    {pub.title}
                                                                </Link>
                                                            </li>
                                                        ))
                                                    ) : (
                                                        <li>Aucune publication récente</li>
                                                    )}
                                                </>
                                            )}
                                            {sectionTitle === 'Calendrier de diffusion' && (
                                                <>
                                                    <li>
                                                        <Link href="calendarTable" underline="hover" sx={{ color: 'black', '&:hover': { textDecoration: 'underline', color: '#FF3D3D' } }}>
                                                            Calendrier de diffusion 2024 - 2027
                                                        </Link>
                                                    </li>
                                                </>
                                            )}
                                        </ul>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                <Typography variant="h5" fontWeight="bold" gutterBottom sx={{
                    backgroundColor: '#f9f9f9'
                }} padding={4} color='black' marginTop={1} textAlign={'center'}>
                    Devise : « En marche avec des statistiques fiables au service du développement ».
                </Typography>
            </Container>
            <Footer />
        </div>
    );
};

export default Home;